// Profile — read-only Workspace identity, Callboard assignment, credentials,
// and notification preferences. The prefs toggles are the ONE allowed direct
// Firestore write (rules open exactly employees/{me}.notificationPrefs).
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { doc, updateDoc } from 'firebase/firestore'
import { DateTime } from 'luxon'
import { db } from '../../firebase'
import { useAuth } from '../../auth/AuthProvider'
import { fetchDepartment, fetchMyCredentials, fetchPositions, fetchTeams } from '../../api/reads'
import { useToast } from '../../components/Toast'
import { Avatar, ErrorBanner, Spinner } from '../../components/ui'
import { formatDateShort } from '../../lib/format'
import { todayLocal, TZ } from '../../lib/time'
import type { Employee } from '../../lib/types'

export default function ProfilePage() {
  const { employee, email, signOutNow } = useAuth()
  if (!employee || !email) return null
  return <Profile employee={employee} email={email} signOutNow={signOutNow} />
}

function credentialStatus(expiresAt: string): { label: string; cls: string } {
  const today = todayLocal()
  const exp = DateTime.fromISO(expiresAt, { zone: TZ })
  const expDate = exp.toISODate() ?? expiresAt.slice(0, 10)
  if (expDate < today) return { label: 'Lapsed', cls: 'bg-danger/10 text-danger' }
  const days = exp.diff(DateTime.fromISO(today, { zone: TZ }), 'days').days
  if (days < 30) return { label: 'Expiring', cls: 'bg-sunset/20 text-clay' }
  return { label: 'Current', cls: 'bg-forest/10 text-forest' }
}

function Toggle({
  id,
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  id: string
  label: string
  hint: string
  checked: boolean
  disabled: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div>
        <label htmlFor={id} className="font-semibold text-ink">
          {label}
        </label>
        <p className="text-xs text-stone">{hint}</p>
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
          checked ? 'bg-forest' : 'bg-stone/40'
        }`}
      >
        <span
          className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${
            checked ? 'left-6' : 'left-1'
          }`}
          aria-hidden
        />
      </button>
    </div>
  )
}

function Profile({
  employee,
  email,
  signOutNow,
}: {
  employee: Employee
  email: string
  signOutNow: () => Promise<void>
}) {
  const toast = useToast()
  const departmentId = employee.departmentId

  const referenceQuery = useQuery({
    queryKey: ['reference', departmentId],
    queryFn: async () => {
      const [teams, positions] = await Promise.all([fetchTeams(departmentId!), fetchPositions(departmentId!)])
      return { teams, positions }
    },
    enabled: !!departmentId,
  })
  const departmentQuery = useQuery({
    queryKey: ['department', departmentId],
    queryFn: () => fetchDepartment(departmentId!),
    enabled: !!departmentId,
  })
  const credentialsQuery = useQuery({
    queryKey: ['credentials', 'mine', email],
    queryFn: () => fetchMyCredentials(email),
  })

  const [prefs, setPrefs] = useState(employee.notificationPrefs)
  const [savingPrefs, setSavingPrefs] = useState(false)

  const setPref = async (key: 'email' | 'openShiftBroadcasts', value: boolean) => {
    const previous = prefs
    const next = { ...prefs, [key]: value }
    setPrefs(next)
    setSavingPrefs(true)
    try {
      // The ONE allowed direct write: my own notificationPrefs.
      await updateDoc(doc(db, 'employees', email), { notificationPrefs: next })
      toast('Notification preferences saved.', 'success')
    } catch (e) {
      setPrefs(previous)
      toast(e instanceof Error ? e.message : 'Could not save your preferences.', 'error')
    } finally {
      setSavingPrefs(false)
    }
  }

  const ws = employee.workspace
  const teams = referenceQuery.data?.teams ?? []
  const positions = referenceQuery.data?.positions ?? []
  const homeTeamName = teams.find((t) => t.id === employee.homeTeamId)?.name ?? null
  const positionNames = employee.positionIds
    .map((id) => positions.find((p) => p.id === id)?.name)
    .filter((n): n is string => !!n)

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <h1 className="sr-only">Profile</h1>

      <section className="card" aria-label="Workspace identity">
        <div className="flex items-center gap-4">
          <Avatar name={ws.displayName} photoUrl={ws.photoUrl} size={64} />
          <div className="min-w-0">
            <p className="truncate text-lg font-bold text-interlochen">{ws.displayName}</p>
            {ws.title && <p className="truncate text-sm text-ink">{ws.title}</p>}
            <p className="truncate text-sm text-stone">{email}</p>
            {(ws.phone || ws.mobilePhone) && (
              <p className="tnum truncate text-sm text-stone">
                {[ws.phone, ws.mobilePhone].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        </div>
        <p className="mt-3 text-xs text-stone">Synced from Google Workspace</p>
      </section>

      <section className="card space-y-2" aria-label="Callboard assignment">
        <p className="section-label">Callboard</p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="chip bg-interlochen/10 capitalize text-interlochen">{employee.role}</span>
          {homeTeamName && <span className="chip bg-lake/20 text-interlochen">{homeTeamName}</span>}
        </div>
        {departmentQuery.data && <p className="text-sm text-ink">Department: {departmentQuery.data.name}</p>}
        <p className="text-sm text-ink">
          Positions:{' '}
          {referenceQuery.isPending ? 'loading…' : positionNames.length > 0 ? positionNames.join(', ') : 'none yet'}
        </p>
      </section>

      <section className="card" aria-label="Credentials">
        <p className="section-label mb-2">Credentials</p>
        {credentialsQuery.isPending ? (
          <Spinner label="Loading credentials…" />
        ) : credentialsQuery.isError ? (
          <ErrorBanner message="Couldn't load your credentials. Try again." />
        ) : credentialsQuery.data.length === 0 ? (
          <p className="text-sm text-stone">No credentials on file.</p>
        ) : (
          <ul className="divide-y divide-hairline">
            {credentialsQuery.data.map((c) => {
              const status = credentialStatus(c.expiresAt)
              return (
                <li key={c.id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-ink">{c.typeName}</p>
                    <p className="tnum text-xs text-stone">expires {formatDateShort(c.expiresAt)}</p>
                  </div>
                  <span className={`chip shrink-0 ${status.cls}`}>{status.label}</span>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section className="card" aria-label="Notifications">
        <p className="section-label mb-1">Notifications</p>
        <Toggle
          id="pref-email"
          label="Email notifications"
          hint="Schedule publishes, changes to your shifts, and request updates."
          checked={prefs.email}
          disabled={savingPrefs}
          onChange={(v) => void setPref('email', v)}
        />
        <Toggle
          id="pref-broadcasts"
          label="Open-shift broadcasts"
          hint="An email when a shift in one of your positions opens up."
          checked={prefs.openShiftBroadcasts}
          disabled={savingPrefs}
          onChange={(v) => void setPref('openShiftBroadcasts', v)}
        />
      </section>

      <button className="btn-secondary w-full sm:w-auto" onClick={() => void signOutNow()}>
        Sign out
      </button>
    </div>
  )
}
